import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AppIcon } from '../../shared/ui/Icon';
import { addDays, format, parseISO, isValid, startOfWeek } from 'date-fns';
import { IoCalendarNumberOutline } from 'react-icons/io5';

const CALENDAR_VIEW_STORAGE_KEY = 'minibook-wb-calendar-view';

type CalendarViewMode = 'minimal' | 'full';

type DayCell = {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isAvailable: boolean;
};

interface CalendarProps {
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
  availableDates?: Set<string> | null;
  occupancyByDate?: Record<string, number>;
  minDate?: string;
  maxDate?: string;
  onMonthChange?: (range: { from: string; to: string }) => void;
  showNearestButton?: boolean;
}

export function Calendar({
  selectedDate,
  onDateSelect,
  availableDates,
  occupancyByDate: _occupancyByDate = undefined,
  minDate,
  maxDate,
  onMonthChange,
  showNearestButton = true,
}: CalendarProps) {
  const { t } = useTranslation();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewMode, setViewMode] = useState<CalendarViewMode>(() => {
    try {
      const v = localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
      if (v === 'full') return 'full';
    } catch {
      /* ignore */
    }
    return 'minimal';
  });

  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = selectedDate ? parseISO(selectedDate) : new Date();
    const base = isValid(date) ? date : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  useEffect(() => {
    try {
      localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  useEffect(() => {
    if (!selectedDate) return;
    const nextDate = parseISO(selectedDate);
    if (!isValid(nextDate)) return;
    const y = nextDate.getFullYear();
    const m = nextDate.getMonth();
    setCurrentMonth((prev) =>
      prev.getFullYear() === y && prev.getMonth() === m ? prev : new Date(y, m, 1)
    );
  }, [selectedDate]);

  /** Границы по календарным месяцам (строка yyyy-MM) — без new Date('…-01') UTC, чтобы ◀▶ и disabled не ломались */
  const minYm = minDate && minDate.length >= 7 ? minDate.slice(0, 7) : null;
  const maxYm = maxDate && maxDate.length >= 7 ? maxDate.slice(0, 7) : null;
  const currentMonthKey = format(currentMonth, 'yyyy-MM');

  useEffect(() => {
    const cur = currentMonthKey;
    if (minYm && cur < minYm) {
      const [y, m] = minYm.split('-').map(Number);
      setCurrentMonth(new Date(y, m - 1, 1));
      return;
    }
    if (maxYm && cur > maxYm) {
      const [y, m] = maxYm.split('-').map(Number);
      setCurrentMonth(new Date(y, m - 1, 1));
    }
  }, [currentMonthKey, minYm, maxYm]);

  useEffect(() => {
    if (!onMonthChange) return;
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    onMonthChange({
      from: format(firstDayOfMonth, 'yyyy-MM-dd'),
      to: format(lastDayOfMonth, 'yyyy-MM-dd'),
    });
  }, [currentMonth, onMonthChange]);

  const weekdays = [
    t('calendar.weekdays.mon'),
    t('calendar.weekdays.tue'),
    t('calendar.weekdays.wed'),
    t('calendar.weekdays.thu'),
    t('calendar.weekdays.fri'),
    t('calendar.weekdays.sat'),
    t('calendar.weekdays.sun'),
  ];

  const months = [
    t('calendar.months.january'),
    t('calendar.months.february'),
    t('calendar.months.march'),
    t('calendar.months.april'),
    t('calendar.months.may'),
    t('calendar.months.june'),
    t('calendar.months.july'),
    t('calendar.months.august'),
    t('calendar.months.september'),
    t('calendar.months.october'),
    t('calendar.months.november'),
    t('calendar.months.december'),
  ];

  const canGoPrev = minYm == null || currentMonthKey > minYm;
  const canGoNext = maxYm == null || currentMonthKey < maxYm;

  const prevMonth = () => {
    if (!canGoPrev) return;
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    if (!canGoNext) return;
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const isDateAvailable = useCallback(
    (date: Date): boolean => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      if (minDate && dateStr < minDate) return false;
      if (maxDate && dateStr > maxDate) return false;

      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if (dateStr < todayStr) return false;

      if (!availableDates) return false;
      return availableDates.has(dateStr);
    },
    [availableDates, maxDate, minDate, today]
  );

  const getDaysInMonth = (): DayCell[] => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const firstDayOfWeek = firstDay.getDay() === 0 ? 7 : firstDay.getDay();

    const days: DayCell[] = [];

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 2; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: selectedDate === dateStr,
        isAvailable: false,
      });
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: selectedDate === dateStr,
        isAvailable: isDateAvailable(date),
      });
    }

    const daysNeeded = 42 - days.length;
    for (let day = 1; day <= daysNeeded; day++) {
      const date = new Date(year, month + 1, day);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: selectedDate === dateStr,
        isAvailable: false,
      });
    }

    return days;
  };

  /** Свёрнутый вид: ряд 1 — календарная неделя с сегодня, ряд 2 — следующая (пн–вс, как шапка) */
  const getTwoCalendarWeeksFromToday = (): DayCell[] => {
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const yM = currentMonth.getFullYear();
    const mM = currentMonth.getMonth();
    const cells: DayCell[] = [];
    for (let i = 0; i < 14; i++) {
      const date = addDays(weekStart, i);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      cells.push({
        date,
        isCurrentMonth: date.getMonth() === mM && date.getFullYear() === yM,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: selectedDate === dateStr,
        isAvailable: isDateAvailable(date),
      });
    }
    return cells;
  };

  const isGridExpanded = viewMode === 'full';
  const days = isGridExpanded ? getDaysInMonth() : getTwoCalendarWeeksFromToday();
  const monthName = months[currentMonth.getMonth()];
  const year = currentMonth.getFullYear();
  const headerLabel = `${monthName} ${year}`;

  const handleDateClick = (day: DayCell) => {
    if (day.isAvailable) {
      const y = day.date.getFullYear();
      const m = day.date.getMonth();
      const dayNum = day.date.getDate();
      const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      if (selectedDate === dateStr) {
        return;
      }
      onDateSelect(dateStr);
    }
  };

  const goToNearestDate = () => {
    if (!availableDates || availableDates.size === 0) return;

    const sortedDates = Array.from(availableDates).sort();
    const nearestDateStr = sortedDates[0];
    if (!nearestDateStr) return;

    const nearestDate = parseISO(nearestDateStr);
    if (!isValid(nearestDate)) return;
    setCurrentMonth(new Date(nearestDate.getFullYear(), nearestDate.getMonth(), 1));
    onDateSelect(nearestDateStr);
  };

  const hasAvailableDates = availableDates && availableDates.size > 0;

  const expandToggleTitle = isGridExpanded ? t('calendar.showTwoWeeks') : t('calendar.showFullMonth');

  return (
    <div className="booking-calendar">
      <div className="booking-calendar-header">
        <div className="booking-calendar-header-nav">
          <div className="booking-calendar-nav-side booking-calendar-nav-side--start">
            <button
              type="button"
              onClick={prevMonth}
              className="booking-calendar-nav-button"
              aria-label={t('calendar.prevMonth')}
              disabled={!canGoPrev}
            >
              <AppIcon name="chevronLeft" size={18} />
            </button>
          </div>
          <span className="booking-calendar-month-label">{headerLabel}</span>
          <div className="booking-calendar-nav-side booking-calendar-nav-side--end">
            <button
              type="button"
              onClick={nextMonth}
              className="booking-calendar-nav-button"
              aria-label={t('calendar.nextMonth')}
              disabled={!canGoNext}
            >
              <AppIcon name="chevronRight" size={18} />
            </button>
          </div>
        </div>
        <div className="booking-calendar-header-actions">
          {showNearestButton && (
            <button
              type="button"
              onClick={goToNearestDate}
              className="booking-calendar-nav-button"
              aria-label={t('calendar.goToNearestDate')}
              title={t('calendar.goToNearestDate')}
              disabled={!hasAvailableDates}
            >
              <IoCalendarNumberOutline size={18} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setViewMode((m) => (m === 'full' ? 'minimal' : 'full'))}
            className="booking-calendar-nav-button"
            aria-label={expandToggleTitle}
            title={expandToggleTitle}
          >
            <AppIcon name={isGridExpanded ? 'chevronDown' : 'chevronUp'} size={18} />
          </button>
        </div>
      </div>

      <div className="booking-calendar-weekdays">
        {weekdays.map((day) => (
          <div key={day} className="booking-calendar-weekday">
            {day}
          </div>
        ))}
      </div>

      <div
        className={
          isGridExpanded ? 'booking-calendar-grid booking-calendar-grid--month' : 'booking-calendar-grid booking-calendar-grid--weeks'
        }
      >
        {days.map((day, index) => {
          const dateStr = format(day.date, 'yyyy-MM-dd');
          return (
            <button
              key={`${dateStr}-${index}`}
              type="button"
              className={`booking-calendar-day ${!day.isCurrentMonth ? 'is-other-month' : ''} ${day.isToday ? 'is-today' : ''} ${
                day.isSelected ? 'is-selected' : ''
              } ${day.isAvailable ? 'is-available' : 'is-unavailable'}`}
              onClick={() => handleDateClick(day)}
              disabled={!day.isAvailable}
              title={day.isAvailable ? dateStr : t('calendar.unavailable')}
            >
              <span className="booking-calendar-day-num">{day.date.getDate()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
